import type { EnvironmentId } from "@cmmd-forge/contracts";

import { readSavedEnvironmentBearerToken, getSavedEnvironmentRecord } from "./catalog";
import type { CloudRuntimeCredentialProvider } from "./cloudRuntimeCredentialProvider";
import * as savedCloud from "./savedCloudEnvironmentLifecycle";

const providers = new Map<EnvironmentId, CloudRuntimeCredentialProvider>();

export interface SavedEnvironmentConnectionTarget {
  readonly httpBaseUrl: string;
  readonly wsBaseUrl: string;
  readonly bearerToken: string;
  readonly runtimeTicket?: string;
  readonly expiresAt?: string | null;
}

export interface SavedEnvironmentConnectionTargetProvider {
  readonly acquire: (options?: {
    readonly forceRefresh?: boolean;
    readonly signal?: AbortSignal;
  }) => Promise<SavedEnvironmentConnectionTarget>;
  readonly invalidate?: () => void;
}

export function acquireRuntimeDescriptor<Descriptor>(
  provider: {
    readonly acquire: (options?: {
      readonly forceRefresh?: boolean;
      readonly signal?: AbortSignal;
    }) => Promise<Descriptor>;
  },
  signal: AbortSignal | undefined,
): Promise<Descriptor> {
  return provider.acquire(signal ? { signal } : {});
}

export function registerCloudRuntimeCredentialProvider(
  environmentId: EnvironmentId,
  provider: CloudRuntimeCredentialProvider,
): void {
  providers.set(environmentId, provider);
}

/**
 * Whether this environment connected through a renewing CMMD runtime provider.
 *
 * Registration happens only on the runtime-backed branch of
 * `createSavedEnvironmentConnection`, so this answers "was a renewing provider
 * built for this connection" without re-deriving it from the record. Re-deriving
 * would be wrong: the catalog can hand the Environment a Sprite between connect
 * and send, and a connection that took the static bearer path at connect time
 * still has no provider to renew.
 */
export function hasCloudRuntimeCredentialProvider(environmentId: EnvironmentId): boolean {
  return providers.has(environmentId);
}

export function unregisterCloudRuntimeCredentialProvider(
  environmentId: EnvironmentId,
  expected?: CloudRuntimeCredentialProvider,
): void {
  const provider = providers.get(environmentId);
  if (expected && provider !== expected) {
    expected.dispose();
    return;
  }
  provider?.dispose();
  providers.delete(environmentId);
}

export async function readEnvironmentBearerToken(
  environmentId: EnvironmentId,
): Promise<string | null> {
  const provider = providers.get(environmentId);
  if (provider) return (await provider.acquire()).bearerToken;
  const record = getSavedEnvironmentRecord(environmentId);
  if (record && savedCloud.isCmmdCloudEnvironment(record)) return null;
  return readSavedEnvironmentBearerToken(environmentId);
}
